import React, { useEffect, useMemo, useState } from "react";
import ReactMarkdown from "react-markdown";
import remarkGfm from "remark-gfm";
import rehypeRaw from "rehype-raw";
import { api } from "@/lib/api";
import { toast } from "sonner";
import { Card, PageHeader, Button, Spinner, Badge } from "@/components/ui";
import { Download, FileText, Users, Clock, GitBranch, Calculator, TrendingUp, Lock, Share2 } from "lucide-react";

const MD_URL = "/docs/GoMCO-Trillium-Delivery-Model.md";

// --- Economic model (mirrors the Delivery & Ops doc; sublinear scaling for economies of scale) ---
const BASE_MEMBERS = 134600;

const MODELS = {
  traditional: {
    baseRunFTE: 9.9, fteExp: 0.6, loadedFTE: 145300, aiBase: 0, aiExp: 0,
    infraBase: 360000, upsellDeliveryRate: 0.35,
    implLabor: 3050000, implMobilization: 600000, contingencyRate: 0.20,
  },
  ai: {
    baseRunFTE: 5.0, fteExp: 0.5, loadedFTE: 159200, aiBase: 260000, aiExp: 0.7,
    infraBase: 360000, upsellDeliveryRate: 0.20,
    implLabor: 1450000, implMobilization: 450000, contingencyRate: 0.20,
  },
};

function pmpmFor(members) {
  if (members <= 150000) return 4.25;
  if (members <= 350000) return 3.75;
  if (members <= 600000) return 3.10;
  if (members <= 1000000) return 2.75;
  return 2.40;
}

function fmtM(n) {
  if (Math.abs(n) >= 1_000_000) return `$${(n / 1_000_000).toFixed(2)}M`;
  if (Math.abs(n) >= 1000) return `$${(n / 1000).toFixed(0)}k`;
  return `$${Math.round(n).toLocaleString()}`;
}
const fmtNum = (n) => Math.round(n).toLocaleString();

async function downloadPdf() {
  try {
    const res = await api.get("/docs/delivery-model/pdf", { responseType: "blob" });
    const url = URL.createObjectURL(res.data);
    const a = document.createElement("a");
    a.href = url; a.download = "GoMCO-Trillium-Delivery-Model.pdf"; a.click();
    URL.revokeObjectURL(url);
  } catch { toast.error("PDF export failed"); }
}

function PnLCalculator() {
  const [members, setMembers] = useState(BASE_MEMBERS);
  const [includeUpsells, setIncludeUpsells] = useState(true);
  const [premiumSLA, setPremiumSLA] = useState(false);
  const [modelKey, setModelKey] = useState("traditional");

  const m = useMemo(() => {
    const cfg = MODELS[modelKey];
    const ratio = members / BASE_MEMBERS;
    const pmpm = pmpmFor(members);
    let subscription = members * pmpm * 12;
    if (premiumSLA) subscription *= 1.08;

    const upsellRev = includeUpsells ? subscription * 0.28 : 0;
    const totalRev = subscription + upsellRev;

    const runFTE = cfg.baseRunFTE * Math.pow(ratio, cfg.fteExp) + (premiumSLA ? 0.6 : 0);
    const labor = runFTE * cfg.loadedFTE;
    const aiCost = cfg.aiBase * Math.pow(ratio, cfg.aiExp);
    const infra = cfg.infraBase * Math.pow(ratio, 0.5);
    const upsellDelivery = upsellRev * cfg.upsellDeliveryRate;
    const costToServe = labor + aiCost + infra + upsellDelivery;

    const grossProfit = totalRev - costToServe;
    const margin = totalRev > 0 ? (grossProfit / totalRev) * 100 : 0;
    const hours = runFTE * 2080;

    // Self-funded implementation (fee = labor + contingency padding + mobilization runway)
    const implContingency = cfg.implLabor * cfg.contingencyRate;
    const implFee = cfg.implLabor + implContingency + cfg.implMobilization;
    const implCost = cfg.implLabor + cfg.implMobilization;
    const implNet = implFee - implCost; // retained padding, >= 0 → never out of pocket

    return { pmpm, subscription, upsellRev, totalRev, runFTE, labor, aiCost, infra, upsellDelivery,
             costToServe, grossProfit, margin, hours, ratio, implFee, implLabor: cfg.implLabor,
             implContingency, implMobilization: cfg.implMobilization, implCost, implNet };
  }, [members, includeUpsells, premiumSLA, modelKey]);

  const growthLabel = m.ratio >= 9.5 ? "≈10× growth" : m.ratio >= 3.8 ? "≈4× growth" : m.ratio > 1.05 ? `${m.ratio.toFixed(1)}× growth` : "Contract baseline";

  return (
    <Card className="p-6 mb-6 border-brand-200" data-testid="pnl-calculator">
      <div className="flex items-center gap-2 mb-1">
        <Calculator className="h-5 w-5 text-brand-700" />
        <h2 className="font-display text-xl font-bold">Interactive Cost-to-Serve &amp; Margin Model</h2>
      </div>
      <p className="text-sm text-muted-foreground mb-4">Switch operating models, slide membership, and toggle scope — FTEs, hours, cost-to-serve, and margin recompute live.</p>

      {/* Operating model toggle */}
      <div className="inline-flex rounded-lg border border-border p-1 mb-5" data-testid="model-toggle">
        {[["traditional", "Traditional Team"], ["ai", "AI-Operated Lean"]].map(([k, label]) => (
          <button key={k} onClick={() => setModelKey(k)} data-testid={`model-${k}`}
            className={`px-4 py-1.5 text-sm font-medium rounded-md transition-colors ${modelKey === k ? "bg-brand-700 text-white" : "text-muted-foreground hover:text-foreground"}`}>
            {label}
          </button>
        ))}
      </div>

      <div className="grid grid-cols-1 lg:grid-cols-5 gap-6">
        {/* Controls */}
        <div className="lg:col-span-2 space-y-5">
          <div>
            <div className="flex items-baseline justify-between">
              <label className="text-xs font-semibold uppercase tracking-wider text-muted-foreground">Managed Members</label>
              <Badge tone="Low">{growthLabel}</Badge>
            </div>
            <p className="text-3xl font-bold font-display text-brand-800 mt-1" data-testid="calc-members">{fmtNum(members)}</p>
            <input
              type="range" min="100000" max="1300000" step="10000"
              value={members} onChange={(e) => setMembers(parseInt(e.target.value))}
              data-testid="calc-members-slider"
              className="w-full mt-2 accent-brand-700 cursor-pointer"
            />
            <div className="flex justify-between text-[10px] text-muted-foreground mt-1">
              <span>100k</span><span>540k (4×)</span><span>1.3M (10×)</span>
            </div>
          </div>

          <div className="flex items-center justify-between rounded-md border border-border p-3">
            <div>
              <p className="text-sm font-medium">Active PMPM tier</p>
              <p className="text-xs text-muted-foreground">Auto-selected from volume tiers</p>
            </div>
            <p className="text-2xl font-bold font-display text-brand-700" data-testid="calc-pmpm">${m.pmpm.toFixed(2)}</p>
          </div>

          <label className="flex items-center justify-between rounded-md border border-border p-3 cursor-pointer">
            <div>
              <p className="text-sm font-medium">Include upsells &amp; retainers</p>
              <p className="text-xs text-muted-foreground">AI, billing, interoperability, retainers (+28%)</p>
            </div>
            <input type="checkbox" checked={includeUpsells} onChange={(e) => setIncludeUpsells(e.target.checked)} data-testid="calc-upsells" className="h-5 w-5 accent-brand-700" />
          </label>

          <label className="flex items-center justify-between rounded-md border border-border p-3 cursor-pointer">
            <div>
              <p className="text-sm font-medium">Premium SLA (24×7)</p>
              <p className="text-xs text-muted-foreground">+8% revenue, adds on-call pod</p>
            </div>
            <input type="checkbox" checked={premiumSLA} onChange={(e) => setPremiumSLA(e.target.checked)} data-testid="calc-premium" className="h-5 w-5 accent-brand-700" />
          </label>
        </div>

        {/* Results */}
        <div className="lg:col-span-3">
          <div className="grid grid-cols-2 sm:grid-cols-4 gap-3">
            {[
              { icon: GitBranch, label: "Run-team FTE", value: m.runFTE.toFixed(1), tid: "res-fte" },
              { icon: Clock, label: "Run hours / yr", value: fmtNum(m.hours), tid: "res-hours" },
              { icon: Users, label: "Annual revenue", value: fmtM(m.totalRev), tid: "res-rev" },
              { icon: TrendingUp, label: "Gross margin", value: `${m.margin.toFixed(1)}%`, tid: "res-margin" },
            ].map((s) => (
              <div key={s.label} className="rounded-md bg-brand-950 text-white p-3">
                <s.icon className="h-4 w-4 text-brand-300 mb-1.5" />
                <p className="text-xl font-bold font-display" data-testid={s.tid}>{s.value}</p>
                <p className="text-[10px] text-brand-100 mt-0.5">{s.label}</p>
              </div>
            ))}
          </div>

          {/* Margin bar */}
          <div className="mt-4">
            <div className="flex justify-between text-xs text-muted-foreground mb-1">
              <span>Cost to serve {fmtM(m.costToServe)}</span>
              <span>Gross profit {fmtM(m.grossProfit)}</span>
            </div>
            <div className="h-6 w-full rounded-full bg-slate-200 overflow-hidden flex">
              <div className="h-full bg-slate-400 flex items-center justify-center text-[10px] text-white font-semibold transition-all duration-300"
                   style={{ width: `${Math.min(100 - m.margin, 100)}%` }}>
                {(100 - m.margin).toFixed(0)}%
              </div>
              <div className="h-full bg-brand-600 flex items-center justify-center text-[10px] text-white font-semibold transition-all duration-300"
                   style={{ width: `${Math.max(m.margin, 0)}%` }}>
                {m.margin.toFixed(0)}%
              </div>
            </div>
          </div>

          {/* Breakdown table */}
          <div className="mt-4 grid grid-cols-1 sm:grid-cols-2 gap-3 text-sm">
            <div className="rounded-md border border-border p-3">
              <p className="text-[10px] uppercase tracking-wider text-muted-foreground mb-2">Revenue (annual)</p>
              <div className="flex justify-between py-0.5"><span className="text-muted-foreground">Base subscription</span><span className="font-mono">{fmtM(m.subscription)}</span></div>
              <div className="flex justify-between py-0.5"><span className="text-muted-foreground">Upsells &amp; retainers</span><span className="font-mono">{fmtM(m.upsellRev)}</span></div>
              <div className="flex justify-between py-1 border-t border-border mt-1 font-semibold"><span>Total</span><span className="font-mono text-brand-700">{fmtM(m.totalRev)}</span></div>
            </div>
            <div className="rounded-md border border-border p-3">
              <p className="text-[10px] uppercase tracking-wider text-muted-foreground mb-2">Cost to serve (annual)</p>
              <div className="flex justify-between py-0.5"><span className="text-muted-foreground">Run-team labor</span><span className="font-mono">{fmtM(m.labor)}</span></div>
              {m.aiCost > 0 && <div className="flex justify-between py-0.5"><span className="text-muted-foreground">AI &amp; automation</span><span className="font-mono">{fmtM(m.aiCost)}</span></div>}
              <div className="flex justify-between py-0.5"><span className="text-muted-foreground">Infrastructure</span><span className="font-mono">{fmtM(m.infra)}</span></div>
              <div className="flex justify-between py-0.5"><span className="text-muted-foreground">Upsell delivery</span><span className="font-mono">{fmtM(m.upsellDelivery)}</span></div>
              <div className="flex justify-between py-1 border-t border-border mt-1 font-semibold"><span>Total</span><span className="font-mono">{fmtM(m.costToServe)}</span></div>
            </div>
          </div>

          {/* Self-funded implementation */}
          <div className="mt-3 rounded-md bg-brand-50 border border-brand-200 p-3 text-sm" data-testid="impl-summary">
            <p className="text-[10px] uppercase tracking-wider text-brand-800 font-semibold mb-2">Self-funded implementation (one-time, milestone-prepaid)</p>
            <div className="grid grid-cols-2 sm:grid-cols-4 gap-2">
              <div><p className="text-[10px] text-muted-foreground">Fee to Trillium</p><p className="font-mono font-semibold text-brand-800" data-testid="impl-fee">{fmtM(m.implFee)}</p></div>
              <div><p className="text-[10px] text-muted-foreground">Labor + mobilization</p><p className="font-mono">{fmtM(m.implCost)}</p></div>
              <div><p className="text-[10px] text-muted-foreground">Contingency padding</p><p className="font-mono">{fmtM(m.implContingency)}</p></div>
              <div><p className="text-[10px] text-muted-foreground">Net out-of-pocket</p><p className="font-mono font-semibold text-indigo-700" data-testid="impl-net">$0 · +{fmtM(m.implNet)} buffer</p></div>
            </div>
          </div>

          <p className="text-[11px] text-muted-foreground mt-3">
            Implementation is invoiced <strong>milestone-prepaid</strong> so Go MCO is <strong>never out-of-pocket</strong> and has runway to hire/contract before spending. Figures are fully-loaded planning estimates and scale sublinearly with membership.
          </p>
        </div>
      </div>
    </Card>
  );
}

const VERSIONS = {
  internal: { label: "Internal (full)", mdUrl: "/docs/GoMCO-Trillium-Delivery-Model.md", pdfKey: "delivery-model", pdfName: "GoMCO-Trillium-Delivery-Model.pdf" },
  external: { label: "Shareable (reframed)", mdUrl: "/docs/GoMCO-Delivery-Approach-External.md", pdfKey: "delivery-approach-external", pdfName: "GoMCO-Delivery-Approach-External.pdf" },
};

export default function DeliveryModel() {
  const [view, setView] = useState("internal");
  const [md, setMd] = useState(null);
  const v = VERSIONS[view];
  const isExternal = view === "external";

  useEffect(() => {
    setMd(null);
    fetch(v.mdUrl).then((r) => r.text()).then(setMd).catch(() => setMd("Failed to load document."));
  }, [v.mdUrl]);

  const downloadCurrentPdf = async () => {
    try {
      const res = await api.get(`/docs/${v.pdfKey}/pdf`, { responseType: "blob" });
      const url = URL.createObjectURL(res.data);
      const a = document.createElement("a");
      a.href = url; a.download = v.pdfName; a.click();
      URL.revokeObjectURL(url);
    } catch { toast.error("PDF export failed"); }
  };

  return (
    <div className="vc-fade-up max-w-5xl">
      <PageHeader
        title="Delivery & Operations Model"
        subtitle="The internal blueprint (staffing, payroll, cost-to-serve, margins) vs. the Trillium-safe delivery approach. Toggle to compare — and know what to submit."
        actions={<div className="flex gap-2">
          <Button variant="outline" onClick={downloadCurrentPdf} data-testid="download-delivery-pdf"><FileText className="h-4 w-4" /> PDF</Button>
          <a href={v.mdUrl} download><Button data-testid="download-delivery-md"><Download className="h-4 w-4" /> Markdown</Button></a>
        </div>}
      />

      {/* Version toggle */}
      <div className="inline-flex rounded-lg border border-border p-1 mb-4" data-testid="delivery-version-toggle">
        {Object.entries(VERSIONS).map(([k, cfg]) => (
          <button key={k} onClick={() => setView(k)} data-testid={`delivery-view-${k}`}
            className={`flex items-center gap-1.5 px-4 py-1.5 text-sm font-medium rounded-md transition-colors ${view === k ? "bg-brand-700 text-white" : "text-muted-foreground hover:text-foreground"}`}>
            {k === "internal" ? <Lock className="h-3.5 w-3.5" /> : <Share2 className="h-3.5 w-3.5" />}
            {cfg.label}
          </button>
        ))}
      </div>

      {isExternal ? (
        <div className="mb-6 rounded-md bg-indigo-50 border border-indigo-200 px-4 py-3 flex items-start gap-2 text-sm text-indigo-800" data-testid="delivery-banner-external">
          <Share2 className="h-4 w-4 mt-0.5 shrink-0" />
          <span><strong>Safe to submit to Trillium.</strong> The reframed "Delivery Approach" — governance, team, timeline, training, and commercial assurances. It omits payroll, cost-to-serve, and margins. <strong>This is the one you hand over.</strong></span>
        </div>
      ) : (
        <div className="mb-6 rounded-md bg-red-50 border border-red-200 px-4 py-3 flex items-start gap-2 text-sm text-red-700" data-testid="delivery-banner-internal">
          <Lock className="h-4 w-4 mt-0.5 shrink-0" />
          <span><strong>Internal only — never share.</strong> Includes payroll, cost-to-serve, margins, and the live P&L calculator. Use it for planning. To submit, switch to <strong>Shareable (reframed)</strong>.</span>
        </div>
      )}

      {!isExternal && <PnLCalculator />}

      <Card className="p-6 sm:p-10">
        {md === null ? <Spinner label="Loading…" /> : (
          <div className="rfp-prose" data-testid="delivery-content">
            <ReactMarkdown remarkPlugins={[remarkGfm]} rehypePlugins={[rehypeRaw]}>{md}</ReactMarkdown>
          </div>
        )}
      </Card>
    </div>
  );
}
